/**
 * Export/Import controls for trip data.
 * Export formats: JSON, PDF, JPG
 * Export destinations: user's computer or Fias file system
 */

import { useState, useRef, useCallback } from 'react';
import { Download, Upload, Loader2, Share2, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { captureError } from '@/lib/telemetry';
import api from '@/lib/api';
import type {
  TravelTripResponse,
  TravelItemResponse,
  TravelEventResponse,
  TravelAttachmentResponse,
} from '../api';
import { TravelCalendarView } from './TravelCalendarView';
import {
  formatDayDate,
  getEventTypeColor,
  EVENT_TYPE_LABELS,
  TRANSIT_MODE_EMOJI,
} from './TravelHelpers';
import { formatTime } from './TravelSettings';

/** Static attachment renderer for export — uses pre-fetched data URLs, no async effects */
function ExportAttachments({
  eventAttachments,
  imageDataUrls,
}: {
  eventAttachments: TravelAttachmentResponse[];
  imageDataUrls: Map<string, string>;
}) {
  if (eventAttachments.length === 0) return null;
  return (
    <div style={{ marginTop: '8px' }}>
      {eventAttachments.map((att) => {
        const dataUrl = imageDataUrls.get(att.fileId);
        if (dataUrl) {
          return (
            <div key={att.attachmentId} style={{ marginBottom: '8px' }}>
              <img
                src={dataUrl}
                alt={att.fileName || ''}
                style={{
                  maxHeight: '300px',
                  maxWidth: '100%',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                }}
              />
              <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                {att.fileName}
              </div>
            </div>
          );
        }
        return (
          <div
            key={att.attachmentId}
            style={{ fontSize: '14px', color: '#888', marginBottom: '4px' }}
          >
            📎 {att.fileName || att.fileId}
          </div>
        );
      })}
    </div>
  );
}

/** Static itinerary for export — renders everything inline with pre-fetched images */
function ExportItinerary({
  trip,
  items,
  events,
  attachments,
  imageDataUrls,
}: {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments: TravelAttachmentResponse[];
  imageDataUrls: Map<string, string>;
}) {
  const eventsByItem = new Map<string, TravelEventResponse[]>();
  for (const ev of events) {
    const list = eventsByItem.get(ev.itemId) || [];
    list.push(ev);
    eventsByItem.set(ev.itemId, list);
  }
  const attsByEvent = new Map<string, TravelAttachmentResponse[]>();
  for (const att of attachments) {
    if (!att.eventId) continue;
    const list = attsByEvent.get(att.eventId) || [];
    list.push(att);
    attsByEvent.set(att.eventId, list);
  }

  return (
    <div>
      {items.map((item) => {
        if (item.itemType === 'transit') {
          return (
            <div
              key={item.itemId}
              style={{ margin: '24px 0', textAlign: 'center', color: '#666', fontSize: '16px' }}
            >
              {item.transitMode && TRANSIT_MODE_EMOJI[item.transitMode]} {item.transitFrom || '?'} →{' '}
              {item.transitTo || '?'}
              {item.transitDeparture && ` (${item.transitDeparture})`}
            </div>
          );
        }
        const dayEvents = eventsByItem.get(item.itemId) || [];
        return (
          <div key={item.itemId} style={{ marginBottom: '36px' }}>
            <div
              style={{
                borderBottom: '3px solid #e5e7eb',
                paddingBottom: '8px',
                marginBottom: '14px',
              }}
            >
              <div style={{ fontSize: '15px', color: '#2563eb', fontWeight: 500 }}>
                {formatDayDate(trip.startDate, item.dayNumber ?? 1)}
              </div>
              <div style={{ fontSize: '26px', fontWeight: 'bold', marginTop: '2px' }}>
                {item.dayTitle || `Day ${item.dayNumber}`}
              </div>
              {item.city && (
                <div style={{ fontSize: '16px', color: '#666', marginTop: '2px' }}>
                  {item.city}
                  {item.country ? `, ${item.country}` : ''}
                </div>
              )}
            </div>
            {item.accommodationName && (
              <div style={{ fontSize: '16px', marginBottom: '12px', marginLeft: '16px' }}>
                🏨 <strong>{item.accommodationName}</strong>
                {item.accommodationAddress && (
                  <span style={{ color: '#666' }}> — {item.accommodationAddress}</span>
                )}
              </div>
            )}
            {dayEvents.length > 0 ? (
              <div style={{ marginLeft: '16px' }}>
                {dayEvents.map((ev) => {
                  const color = getEventTypeColor(ev.eventType);
                  const evAtts = attsByEvent.get(ev.eventId) || [];
                  return (
                    <div
                      key={ev.eventId}
                      style={{
                        display: 'flex',
                        gap: '12px',
                        marginBottom: '12px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          width: '60px',
                          textAlign: 'right',
                          fontSize: '15px',
                          color: '#888',
                          flexShrink: 0,
                          fontFamily: 'monospace',
                        }}
                      >
                        {ev.time ? formatTime(ev.time) : '—'}
                      </div>
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: color,
                          marginTop: '7px',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '17px', fontWeight: 500 }}>{ev.title}</span>
                        <span
                          style={{
                            fontSize: '12px',
                            marginLeft: '8px',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            backgroundColor: color + '20',
                            color,
                          }}
                        >
                          {EVENT_TYPE_LABELS[ev.eventType]}
                        </span>
                        {ev.notes && (
                          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                            {ev.notes}
                          </div>
                        )}
                        <ExportAttachments
                          eventAttachments={evAtts}
                          imageDataUrls={imageDataUrls}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                style={{ fontSize: '15px', color: '#bbb', marginLeft: '16px', fontStyle: 'italic' }}
              >
                No events planned
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

type ExportFormat = 'json' | 'pdf' | 'jpg';
type ExportDestination = 'computer' | 'fias';

interface TravelExportImportProps {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments?: TravelAttachmentResponse[];
  onImport: (data: Record<string, unknown>) => Promise<unknown>;
  shareEnabled?: boolean;
  onOpenShare?: () => void;
}

function slugify(title: string): string {
  return title.replace(/[^a-z0-9]/gi, '-').toLowerCase();
}

/** Upload a blob to the Fias file system via presigned URL */
async function uploadToFias(blob: Blob, fileName: string, mimeType: string): Promise<void> {
  const presigned = await api.getUploadUrl({
    fileName,
    mimeType,
    sizeBytes: blob.size,
    category: 'general',
  });

  const putResponse = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': mimeType },
    body: blob,
  });
  if (!putResponse.ok) throw new Error(`Upload failed (${putResponse.status})`);

  await api.confirmUpload(presigned.fileId, {
    fileName,
    mimeType,
    sizeBytes: blob.size,
    s3Key: presigned.s3Key,
    category: 'general',
    tags: ['travel', 'export'],
  });
}

/** Download a blob to the user's computer */
function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export function TravelExportImport({
  trip,
  items,
  events,
  attachments = [],
  onImport,
  shareEnabled = false,
  onOpenShare,
}: TravelExportImportProps) {
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [destination, setDestination] = useState<ExportDestination>('computer');
  const [includeCalendar, setIncludeCalendar] = useState(true);
  const [includeItinerary, setIncludeItinerary] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState<Map<string, string>>(new Map());
  const renderRef = useRef<HTMLDivElement>(null);

  const needsViewSelection = format === 'pdf' || format === 'jpg';
  const slug = slugify(trip.title);

  // Build the JSON export data
  const buildJsonData = useCallback(() => {
    const eventsByItem = new Map<string, TravelEventResponse[]>();
    for (const event of events) {
      const list = eventsByItem.get(event.itemId) || [];
      list.push(event);
      eventsByItem.set(event.itemId, list);
    }
    return {
      version: 2,
      startDate: trip.startDate,
      title: trip.title,
      items: items.map((item) => {
        if (item.itemType === 'day') {
          return {
            itemType: 'day',
            dayTitle: item.dayTitle,
            city: item.city,
            country: item.country,
            accommodationName: item.accommodationName,
            accommodationAddress: item.accommodationAddress,
            accommodationCheckin: item.accommodationCheckin,
            accommodationCheckout: item.accommodationCheckout,
            events: (eventsByItem.get(item.itemId) || []).map((ev) => ({
              time: ev.time,
              title: ev.title,
              eventType: ev.eventType,
              notes: ev.notes,
            })),
          };
        }
        return {
          itemType: 'transit',
          transitFrom: item.transitFrom,
          transitTo: item.transitTo,
          transitMode: item.transitMode,
          transitDeparture: item.transitDeparture,
          transitArrival: item.transitArrival,
          transitNotes: item.transitNotes,
        };
      }),
    };
  }, [trip, items, events]);

  /** Pre-fetch all image attachments as data URLs keyed by fileId */
  const prefetchImageAttachments = useCallback(async (): Promise<Map<string, string>> => {
    const dataUrls = new Map<string, string>();
    const imageAtts = attachments.filter((a) => a.mimeType && a.mimeType.startsWith('image/'));
    await Promise.all(
      imageAtts.map(async (att) => {
        try {
          const { downloadUrl } = await api.getDownloadUrl(att.fileId);
          const resp = await fetch(downloadUrl);
          const blob = await resp.blob();
          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          dataUrls.set(att.fileId, dataUrl);
        } catch {
          // skip
        }
      }),
    );
    return dataUrls;
  }, [attachments]);

  // Render the offscreen view and capture as canvas
  const captureCanvas = useCallback(async (): Promise<HTMLCanvasElement> => {
    // Pre-fetch all images before rendering so they're available as data URLs
    const imageDataUrls = await prefetchImageAttachments();
    setPreloadedImages(imageDataUrls);

    // Wait for React to re-render with preloaded images
    await new Promise((r) => setTimeout(r, 500));

    if (!renderRef.current) throw new Error('Render container not ready');
    const el = renderRef.current;

    // Temporarily move on-screen for html2canvas (it needs layout)
    const prevLeft = el.style.left;
    el.style.left = '0px';
    el.style.zIndex = '-1';

    const html2canvas = (await import('html2canvas-pro')).default;
    const canvas = await html2canvas(el, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
      width: 900,
      windowWidth: 900,
    });

    el.style.left = prevLeft;
    el.style.zIndex = '';

    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('Captured canvas is empty');
    }
    return canvas;
  }, [prefetchImageAttachments]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      if (format === 'json') {
        const data = buildJsonData();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const fileName = `trip-${slug}.json`;

        if (destination === 'fias') {
          await uploadToFias(blob, fileName, 'application/json');
          toast({ description: `Saved "${fileName}" to Fias Files` });
        } else {
          downloadBlob(blob, fileName);
        }
      } else if (format === 'jpg') {
        const canvas = await captureCanvas();
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to create JPG'))),
            'image/jpeg',
            0.92,
          );
        });
        const fileName = `trip-${slug}.jpg`;

        if (destination === 'fias') {
          await uploadToFias(blob, fileName, 'image/jpeg');
          toast({ description: `Saved "${fileName}" to Fias Files` });
        } else {
          downloadBlob(blob, fileName);
        }
      } else if (format === 'pdf') {
        const canvas = await captureCanvas();
        const { default: jsPDF } = await import('jspdf');

        // Convert canvas to JPEG blob, then to Uint8Array for jsPDF
        // (avoids huge base64 data URL strings that can fail on large canvases)
        const jpegBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to create PDF image'))),
            'image/jpeg',
            0.92,
          );
        });
        const imgBytes = new Uint8Array(await jpegBlob.arrayBuffer());

        // Canvas is at 2x scale, so visual dimensions are half
        const visW = canvas.width / 2;
        const visH = canvas.height / 2;

        const pdf = new jsPDF({
          orientation: visW > visH ? 'landscape' : 'portrait',
          unit: 'px',
          format: [visW, visH],
        });
        pdf.addImage(imgBytes, 'JPEG', 0, 0, visW, visH);

        const fileName = `trip-${slug}.pdf`;
        const pdfBlob = pdf.output('blob');
        if (destination === 'fias') {
          await uploadToFias(pdfBlob, fileName, 'application/pdf');
          toast({ description: `Saved "${fileName}" to Fias Files` });
        } else {
          downloadBlob(pdfBlob, fileName);
        }
      }
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        component: 'TravelExportImport',
        action: 'export',
      });
      toast({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
      setShowExportDialog(false);
    }
  }, [format, destination, slug, buildJsonData, captureCanvas]);

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        if (data.days && !data.items) {
          data.items = data.days.map((day: Record<string, unknown>) => ({
            ...day,
            itemType: 'day',
            dayTitle: day.title,
          }));
        }
        await onImport(data);
        setImportStatus('Imported successfully!');
        setTimeout(() => setImportStatus(null), 3000);
      } catch {
        setImportStatus('Import failed — invalid file');
        setTimeout(() => setImportStatus(null), 3000);
      }
    };
    input.click();
  };

  const noop = () => {};

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" title="Share, export or import trip">
            <Share2 className="h-3 w-3 mr-1" /> Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {shareEnabled && onOpenShare && (
            <>
              <DropdownMenuItem onSelect={onOpenShare}>
                <LinkIcon className="h-4 w-4 mr-2" /> Share link…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onSelect={() => setShowExportDialog(true)}>
            <Download className="h-4 w-4 mr-2" /> Export trip…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleImport}>
            <Upload className="h-4 w-4 mr-2" /> Import trip…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {importStatus && (
        <span className="text-xs text-muted-foreground animate-pulse">{importStatus}</span>
      )}

      {/* Export dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Export Trip</DialogTitle>
            <DialogDescription>
              Choose the format and destination for your export.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Format */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Format
              </label>
              <Select value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF (printable)</SelectItem>
                  <SelectItem value="jpg">JPG (image)</SelectItem>
                  <SelectItem value="json">JSON (data)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Destination */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Save to
              </label>
              <Select
                value={destination}
                onValueChange={(v) => setDestination(v as ExportDestination)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="computer">My Computer</SelectItem>
                  <SelectItem value="fias">Fias Files</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* View selection for PDF/JPG */}
            {needsViewSelection && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Include views
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeCalendar}
                      onChange={(e) => setIncludeCalendar(e.target.checked)}
                      className="rounded border-input"
                    />
                    <span className="text-sm">Calendar</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeItinerary}
                      onChange={(e) => setIncludeItinerary(e.target.checked)}
                      className="rounded border-input"
                    />
                    <span className="text-sm">Itinerary</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={exporting || (needsViewSelection && !includeCalendar && !includeItinerary)}
              onClick={handleExport}
            >
              {exporting ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Exporting...
                </>
              ) : (
                <>
                  <Download className="h-3 w-3 mr-1" /> Export
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Render container for PDF/JPG capture — positioned off-screen to the left
           but with visibility preserved so html2canvas can read computed styles */}
      {showExportDialog && needsViewSelection && (
        <div
          ref={renderRef}
          style={{
            position: 'absolute',
            left: '-10000px',
            top: 0,
            width: '900px',
            backgroundColor: '#ffffff',
            color: '#000000',
            padding: '40px',
            fontFamily: 'system-ui, sans-serif',
            overflow: 'visible',
          }}
        >
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            {trip.title}
          </h1>
          <p style={{ fontSize: '18px', color: '#666', marginBottom: '32px' }}>
            {items.filter((i) => i.itemType === 'day').length} days &middot; {events.length} events
          </p>

          {includeCalendar && (
            <div style={{ marginBottom: includeItinerary ? '32px' : 0 }}>
              <h2
                style={{
                  fontSize: '22px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  borderBottom: '2px solid #e5e7eb',
                  paddingBottom: '10px',
                }}
              >
                Calendar
              </h2>
              <TravelCalendarView
                trip={trip}
                items={items}
                events={events}
                attachments={attachments}
                onSelectItem={noop}
                onSwitchToPlanner={noop}
              />
            </div>
          )}

          {includeItinerary && (
            <div>
              <h2
                style={{
                  fontSize: '22px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  borderBottom: '2px solid #e5e7eb',
                  paddingBottom: '10px',
                }}
              >
                Itinerary
              </h2>
              <ExportItinerary
                trip={trip}
                items={items}
                events={events}
                attachments={attachments}
                imageDataUrls={preloadedImages}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
